import {
  CommunicationAudienceType,
  RunAtType,
  TargetedAtTypeNames,
} from "../../config/enums";
import { RadioFieldOption } from "../../blocks/fields/RadioField.types";
import { PicklistOption } from "../../blocks/fields/PicklistField.types";
import { VisibilityRule } from "../../config/visibilityRules";
import { PageRecord, RecordAdapterContext } from "../recordDefinition.types";
import { AudienceRule } from "./audienceRules";

export const audienceEntityName = "g4c_communicationaudience";

const audienceTypeDescriptions: Record<string, string> = {
  Marketing: "Leaves out contacts who have opted out of email.",
  Transactional: "Sends to everyone, including contacts who have opted out.",
};

export const audienceTypeSummary = (record: PageRecord) => {
  const option = audienceTypeOptions.find(
    (entry) => String(entry.value) === String(record.g4c_communicationaudiencetype)
  );

  return option ? `${option.label} - ${option.description}` : "";
};

export const audienceTypeOptions: RadioFieldOption[] = Object.entries(
  CommunicationAudienceType
).map(([label, value]) => ({
  label,
  value: value as number,
  description: audienceTypeDescriptions[label],
}));

export const audienceEntityOptions: PicklistOption[] = Object.entries(
  TargetedAtTypeNames
).map(([label, value]) => ({ Key: value as string, Value: label }));

export const audienceEntityRadioOptions: RadioFieldOption[] = [
  { label: "Account", value: TargetedAtTypeNames.Account, description: "Rules are built on accounts." },
  { label: "Contact", value: TargetedAtTypeNames.Contact, description: "Rules are built on contacts." },
];

export const audienceRunAtOptions: PicklistOption[] = [
  { Key: RunAtType.CRM, Value: "CRM" },
  { Key: RunAtType.Warehouse, Value: "Warehouse" },
];

export const audienceRunAtRadioOptions: RadioFieldOption[] = [
  { label: "CRM", value: RunAtType.CRM, description: "Built from the records in your CRM." },
  { label: "Warehouse", value: RunAtType.Warehouse, description: "Built from the data in your warehouse." },
];

export const audienceRulesOf = (record: PageRecord | null): AudienceRule[] =>
  Array.isArray(record?.rule_data) ? (record?.rule_data as AudienceRule[]) : [];

export const audienceHasRules = (record: PageRecord) =>
  audienceRulesOf(record).length > 0;

export const audienceVisibilityRules: Record<string, VisibilityRule> = {
  // What Regarding and Run At are fixed by: the rules chosen from them, and the audience
  // having been saved. Once saved they stay fixed, even if every rule is removed.
  audienceLocked: (record) => Boolean(record.id) || audienceHasRules(record),
  audienceUnlocked: (record) => !record.id && !audienceHasRules(record),
  warehouseAudienceEnabled: (_, config) =>
    Boolean(config.WarehouseCommunicationAudienceEnabled),
};

/** Warehouse only where the organisation asked for it and has it, as the legacy page chose. */
export function newAudienceRecord({ config }: RecordAdapterContext): PageRecord {
  const warehouse =
    Boolean(config?.WarehouseCommunicationAudienceEnabled) &&
    config?.EmailMarketingDefaultRunAt === RunAtType.Warehouse;

  return {
    g4c_communicationaudiencetype: CommunicationAudienceType.Marketing,
    g4c_entitytype: TargetedAtTypeNames.Contact,
    g4c_runat: warehouse ? RunAtType.Warehouse : RunAtType.CRM,
    rule_data: [],
  };
}

export const adaptAudience = (record: PageRecord): PageRecord => ({
  ...record,
  g4c_runat: record.g4c_runat || RunAtType.CRM,
  rule_data: audienceRulesOf(record),
});

/** The attributes Create and Update take. `rule_data` has to be there, or the service refuses. */
export function toAudiencePayload(record: PageRecord) {
  return {
    g4c_name: record.g4c_name,
    g4c_communicationaudiencetype: Number(record.g4c_communicationaudiencetype),
    g4c_entitytype: record.g4c_entitytype,
    g4c_runat: record.g4c_runat || RunAtType.CRM,
    rule_data: audienceRulesOf(record),
    // Only when the save has counted the rules, so an unchanged audience keeps its count.
    ...(record.audience_count_taken
      ? {
          g4c_lastcount: record.g4c_lastcount ?? null,
          g4c_lastcountedon: record.g4c_lastcountedon ?? null,
        }
      : {}),
  };
}
